import { useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

const coverages = [
  {
    icon: 'ri-fire-line',
    title: 'Incêndio e Explosão',
    description: 'Cobertura completa contra danos causados por incêndio, explosão e queda de raio diretamente no imóvel.',
    items: ['Estrutura física', 'Equipamentos e maquinários', 'Estoque e mercadorias', 'Instalações elétricas'],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Danos ao Imóvel',
    description: 'Proteção para o patrimônio imobiliário da empresa, incluindo reformas e benfeitorias realizadas.',
    items: ['Edificação principal', 'Anexos e galpões', 'Benfeitorias', 'Instalações fixas'],
  },
  {
    icon: 'ri-computer-line',
    title: 'Equipamentos Eletrônicos',
    description: 'Cobertura específica para equipamentos de informática, servidores e sistemas eletrônicos críticos.',
    items: ['Servidores e computadores', 'Sistemas de automação', 'Equipamentos de comunicação', 'Painéis elétricos'],
  },
  {
    icon: 'ri-store-2-line',
    title: 'Perda de Lucros',
    description: 'Indenização pelo lucro cessante durante o período de interrupção das atividades após sinistro.',
    items: ['Lucro bruto cessante', 'Despesas fixas continuadas', 'Período de indenização', 'Recomposição do faturamento'],
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Responsabilidade Civil',
    description: 'Proteção contra danos causados a terceiros em decorrência de incêndio originado no imóvel segurado.',
    items: ['Danos a vizinhos', 'Danos corporais a terceiros', 'Danos materiais a terceiros', 'Despesas de defesa'],
  },
  {
    icon: 'ri-tools-line',
    title: 'Despesas Extraordinárias',
    description: 'Cobertura para custos emergenciais necessários para manter a operação após um sinistro.',
    items: ['Aluguel temporário', 'Remoção de entulho', 'Honorários de peritos', 'Salvamento de bens'],
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Diagnóstico Patrimonial',
    duration: '2–3 dias úteis',
    description: 'Levantamento completo do patrimônio empresarial, identificação de riscos específicos e avaliação do valor em risco para dimensionamento correto das coberturas.',
    deliverables: ['Inventário patrimonial detalhado', 'Mapa de riscos operacionais', 'Avaliação do valor em risco', 'Relatório de exposição'],
  },
  {
    number: '02',
    title: 'Estruturação das Coberturas',
    duration: '1–2 dias úteis',
    description: 'Definição técnica das coberturas necessárias, limites adequados e condições especiais com base no perfil de risco identificado.',
    deliverables: ['Proposta de coberturas personalizada', 'Limites e sublimites recomendados', 'Condições especiais identificadas', 'Comparativo de opções'],
  },
  {
    number: '03',
    title: 'Negociação com Seguradoras',
    duration: '3–5 dias úteis',
    description: 'Cotação simultânea com múltiplas seguradoras, negociação de condições e apresentação de proposta com melhor custo-benefício.',
    deliverables: ['Cotações comparativas', 'Análise de condições gerais', 'Recomendação fundamentada', 'Proposta final negociada'],
  },
  {
    number: '04',
    title: 'Implantação e Acompanhamento',
    duration: 'Contínuo',
    description: 'Emissão da apólice, revisão anual das coberturas e suporte completo em caso de sinistro para garantir indenização justa e ágil.',
    deliverables: ['Emissão e conferência da apólice', 'Revisão anual de coberturas', 'Suporte em sinistros', 'Relatórios periódicos'],
  },
];

const useCases = [
  {
    icon: 'ri-store-3-line',
    title: 'Comércio e Varejo',
    color: 'orange',
    description: 'Lojas, supermercados e centros comerciais com alto volume de estoque e fluxo de pessoas, expostos a riscos de incêndio e danos elétricos.',
    risks: ['Sobrecarga elétrica por equipamentos', 'Incêndio em estoque de alto valor', 'Interrupção de atividades comerciais', 'Danos a clientes e fornecedores'],
  },
  {
    icon: 'ri-building-4-line',
    title: 'Indústria e Manufatura',
    color: 'petrol',
    description: 'Fábricas e plantas industriais com maquinários de alto valor, processos produtivos com risco de explosão e grandes volumes de matéria-prima.',
    risks: ['Explosão em processos produtivos', 'Incêndio em maquinários', 'Perda de produção e lucros', 'Contaminação e danos ambientais'],
  },
  {
    icon: 'ri-hotel-line',
    title: 'Hotelaria e Hospitalidade',
    color: 'green',
    description: 'Hotéis, pousadas e restaurantes com alta ocupação humana, cozinhas industriais e sistemas elétricos complexos.',
    risks: ['Incêndio em cozinha industrial', 'Danos a hóspedes e clientes', 'Interrupção de receitas', 'Responsabilidade civil ampliada'],
  },
  {
    icon: 'ri-hospital-line',
    title: 'Saúde e Clínicas',
    color: 'orange',
    description: 'Hospitais, clínicas e laboratórios com equipamentos médicos de alto valor, substâncias inflamáveis e necessidade de operação contínua.',
    risks: ['Danos a equipamentos médicos', 'Interrupção de atendimentos', 'Perda de medicamentos e insumos', 'Responsabilidade com pacientes'],
  },
];

const benefits = [
  {
    icon: 'ri-search-eye-line',
    title: 'Análise Técnica Especializada',
    description: 'Avaliação profissional do patrimônio e riscos, garantindo coberturas adequadas sem lacunas ou excessos.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Economia Real Comprovada',
    description: 'Negociação direta com seguradoras resulta em prêmios até 35% menores com coberturas superiores.',
  },
  {
    icon: 'ri-time-line',
    title: 'Agilidade no Sinistro',
    description: 'Suporte completo na regulação de sinistros para garantir indenização justa no menor prazo possível.',
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Apólice Sem Surpresas',
    description: 'Revisão detalhada das condições gerais para eliminar cláusulas restritivas e garantias inadequadas.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Revisão Anual Proativa',
    description: 'Atualização periódica das coberturas conforme crescimento e mudanças no patrimônio da empresa.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Suporte Dedicado',
    description: 'Atendimento personalizado com consultor exclusivo para todas as necessidades relacionadas ao seguro.',
  },
];

export default function IncendioEmpresarialPage() {
  const coveragesRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);
  const casesRef = useRef<HTMLElement>(null);
  const benefitsRef = useRef<HTMLElement>(null);

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Seguro Incêndio Empresarial',
    provider: {
      '@type': 'Organization',
      name: 'IBX Brasil',
    },
    description:
      'Consultoria especializada em seguros de incêndio empresarial com análise de riscos, dimensionamento de coberturas e proteção patrimonial completa.',
    serviceType: 'Consultoria em Seguros Incêndio Empresarial',
    areaServed: 'BR',
  };

  return (
    <>
      <SEOHead
        title="Seguro Incêndio Empresarial | Proteção Patrimonial Completa"
        description="Proteja seu patrimônio com consultoria especializada em seguros de incêndio empresarial. Análise de riscos, dimensionamento de coberturas e proteção completa para sua empresa."
        canonical="https://ibxbrasil.com.br/servicos/incendio-empresarial"
        ogImage="https://readdy.ai/api/search-image?query=professional%20business%20building%20with%20fire%20safety%20systems%20modern%20corporate%20facility%20protected%20by%20insurance%20security%20measures%20professional%20architecture%20clean%20background%20realistic%20corporate%20photography&width=1200&height=630&seq=seo-incendio-empresarial-service&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <PageHero
          badge="ASSESSORIA ESPECIALIZADA • INCÊNDIO EMPRESARIAL"
          title="Proteja seu patrimônio empresarial com análise técnica completa"
          highlightedWord="patrimônio"
          highlightColor="orange"
          subtitle="Avaliação patrimonial detalhada, estruturação de coberturas adequadas e negociação estratégica para garantir proteção real contra incêndio e riscos relacionados."
          ctaText="Solicitar Análise Patrimonial"
          ctaLink="https://wa.me/5511976565040?text=Ol%C3%A1!%20Preciso%20de%20assessoria%20em%20*Inc%C3%AAndio%20Empresarial*.%0A%0AGostaria%20de%20realizar%20uma%20*an%C3%A1lise%20patrimonial%20detalhada*%20e%20avaliar%20os%20*riscos%20operacionais*%20da%20minha%20empresa.%0A%0APodemos%20conversar%3F"
          ctaSecondary={{
            text: 'Ver outros serviços',
            link: '/servicos',
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=modern%20industrial%20warehouse%20with%20fire%20protection%20systems%20sprinklers%20and%20safety%20equipment%20professional%20corporate%20environment%20with%20organized%20storage%20and%20clean%20facilities%20orange%20safety%20theme&width=1920&height=700&seq=incendio-hero-bg-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Coberturas Analisadas */}
        <section ref={coveragesRef} className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Coberturas Analisadas pela IBX Brasil
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Avaliação técnica completa de todas as coberturas necessárias para proteção patrimonial empresarial.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coverages.map((coverage, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${coverage.icon} text-2xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{coverage.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{coverage.description}</p>
                  <ul className="space-y-2">
                    {coverage.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-orange-600"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Processo em 4 Etapas */}
        <section ref={processRef} className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Processo de Assessoria em 4 Etapas
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Metodologia estruturada para garantir proteção adequada e economia real.
              </p>
            </div>

            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-2xl">{step.number}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 sm:mb-0">{step.title}</h3>
                        <span className="inline-flex items-center gap-2 text-sm text-orange-600 font-semibold">
                          <i className="ri-time-line"></i>
                          {step.duration}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{step.description}</p>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-file-list-3-line text-orange-600"></i>
                          Entregas desta etapa:
                        </h4>
                        <ul className="space-y-1">
                          {step.deliverables.map((deliverable, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                              <i className="ri-checkbox-circle-fill text-orange-600 text-xs"></i>
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Casos de Aplicação */}
        <section ref={casesRef} className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Para Quais Empresas é Indicado?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A assessoria de incêndio empresarial é essencial para diversos segmentos com exposição patrimonial.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${
                    useCase.color === 'orange'
                      ? 'from-orange-50 to-orange-100/50 border-orange-200'
                      : useCase.color === 'petrol'
                      ? 'from-petrol-50 to-petrol-100/50 border-petrol-200'
                      : 'from-green-50 to-green-100/50 border-green-200'
                  } rounded-2xl p-8 border-2 hover:shadow-xl transition-all`}
                >
                  <div
                    className={`w-14 h-14 ${
                      useCase.color === 'orange'
                        ? 'bg-orange-600'
                        : useCase.color === 'petrol'
                        ? 'bg-petrol-600'
                        : 'bg-green-600'
                    } rounded-xl flex items-center justify-center mb-6`}
                  >
                    <i className={`${useCase.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{useCase.description}</p>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-alert-line text-orange-600"></i>
                      Riscos típicos deste segmento:
                    </h4>
                    <ul className="space-y-2">
                      {useCase.risks.map((risk, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-arrow-right-s-line text-orange-600"></i>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefícios */}
        <section ref={benefitsRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Benefícios da Assessoria IBX Brasil
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Vantagens concretas de contar com assessoria especializada em incêndio empresarial.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${benefit.icon} text-xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Proteja Seu Patrimônio com Segurança Total
            </h2>
            <p className="text-xl text-orange-100 mb-10 max-w-2xl mx-auto">
              Solicite uma análise patrimonial gratuita e descubra como estruturar proteção adequada com economia real.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/5511976565040?text=Ol%C3%A1!%20Preciso%20de%20assessoria%20em%20*Inc%C3%AAndio%20Empresarial*.%0A%0AGostaria%20de%20realizar%20uma%20*an%C3%A1lise%20patrimonial%20detalhada*%20e%20avaliar%20os%20*riscos%20operacionais*%20da%20minha%20empresa.%0A%0APodemos%20conversar%3F"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full sm:w-auto bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Solicitar Análise Patrimonial
              </a>
              <a
                href="/servicos"
                className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Ver Outros Serviços
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
